import useIsCompactViewport from '@Guidelines/Component/FilterPanel/Hooks/useIsCompactViewport';
import formatEuros from '@UI/Helpers/formatEuros';
import AmountCell from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/AmountCell';
import CostCard from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/CostCard';
import CostListState from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/CostListState';
import CostSummaryCard from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/CostSummaryCard';
import formatQuantity from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Helpers/formatQuantity';
import formatShortDateTime from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Helpers/formatShortDateTime';
import formatTokens from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Helpers/formatTokens';
import resolveAudioLabel from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Helpers/resolveAudioLabel';
import useAiCostCalls from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Hooks/useAiCostCalls';
import useAiCostSummaries from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/Hooks/useAiCostSummaries';
import NameCell from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/NameCell';
import ShowMoreButton from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/ShowMoreButton';
import type AiCostCallView from '@UI/Store/AiCosts/Views/AiCostCallView';
import { css } from '@emotion/react';
import costTableStyles from '@UI/Pages/Administration/AiCosts/CostTabs/Shared/costTableStyles';
import * as React from 'react';
import { Column, DataTable } from '@verclam/ui-kit';

const ROWS_PER_PAGE = 25;

const cardsStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.625rem;
`;

type Props = {
	readonly accountId: number;
};

/** The document or interview a call was made on; a call without one belongs to no processing. */
const resolveProcessingLabel = (call: AiCostCallView): string => {
	const related = call.related;

	if (related === null) {
		return '—';
	}

	if (related.kind === 'document') {
		return related.label ?? 'Document supprimé';
	}

	return resolveAudioLabel(related.interviewKind, related.date);
};

const resolveCostLabel = (call: AiCostCallView): string =>
	call.costAmount === null ? 'Sans tarif' : formatEuros(call.costAmount);

/** The individual AI calls, for the super administrator. Mounted under the query key of its tab. */
const CallCosts: React.FC<Props> = ({ accountId }) => {
	const { list, error, refetch, showMore, isLoadingMore } = useAiCostCalls(accountId, ROWS_PER_PAGE);
	const { total } = useAiCostSummaries(accountId);
	const isCompact = useIsCompactViewport();

	const renderOccurredAt = React.useCallback(
		(call: AiCostCallView) => <NameCell title={formatShortDateTime(call.occurredAt)} />,
		[],
	);

	const renderStep = React.useCallback((call: AiCostCallView) => <NameCell title={call.usage.label} />, []);

	const renderProcessing = React.useCallback(
		(call: AiCostCallView) => <NameCell title={resolveProcessingLabel(call)} />,
		[],
	);

	const renderTokens = React.useCallback(
		(call: AiCostCallView) => <AmountCell label={formatTokens(call.quantities?.totalTokens ?? null)} />,
		[],
	);

	const renderCost = React.useCallback(
		(call: AiCostCallView) => (
			<AmountCell
				label={resolveCostLabel(call)}
				detail={call.isEstimatedAfterwards ? 'estimé a posteriori' : null}
			/>
		),
		[],
	);

	return (
		<CostCard title="Appels" subtitle="Réservé au super administrateur. Les plus récents en premier.">
			<CostListState list={list} error={error} onRetry={refetch}>
				{(loaded) => (
					<React.Fragment>
						{isCompact ? (
							<div css={cardsStyle}>
								{loaded.list.map((call) => (
									<CostSummaryCard
										key={call.id}
										title={call.usage.label}
										amount={resolveCostLabel(call)}
										lines={[
											`${formatShortDateTime(call.occurredAt)} · ${resolveProcessingLabel(call)}`,
											`${formatTokens(call.quantities?.totalTokens ?? null)} jetons`,
										]}
									/>
								))}
							</div>
						) : (
							<DataTable
								value={loaded.list}
								dataKey="id"
								ariaLabel="Appels d'IA de la période"
								pt={costTableStyles.pt}
							>
								<Column
									header="Date et heure"
									body={renderOccurredAt}
									footer={
										total === null ? undefined : (
											<NameCell
												title="Total"
												detail={formatQuantity(
													total.callCount ?? loaded.totalRecords,
													'appel',
													'appels',
												)}
											/>
										)
									}
								/>
								<Column header="Étape" body={renderStep} />
								<Column header="Traitement" body={renderProcessing} />
								<Column
									header="Jetons"
									align="right"
									body={renderTokens}
									footer={
										total === null ? undefined : (
											<AmountCell
												label={formatTokens(total.quantities?.totalTokens ?? null)}
											/>
										)
									}
								/>
								<Column
									header="Coût"
									align="right"
									body={renderCost}
									footer={
										total === null ? undefined : (
											<AmountCell label={formatEuros(total.costAmount)} />
										)
									}
								/>
							</DataTable>
						)}
						<ShowMoreButton
							hasMore={loaded.hasMore}
							remainingCount={loaded.remainingCount}
							rows={ROWS_PER_PAGE}
							isLoading={isLoadingMore}
							onShowMore={showMore}
						/>
					</React.Fragment>
				)}
			</CostListState>
		</CostCard>
	);
};

export default CallCosts;
